import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../firebase";

import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import "./CryptoWallet.css";

/*
|--------------------------------------------------------------------------
| SERVER URL
|--------------------------------------------------------------------------
*/

const SERVER_URL = "https://bfctrade-backend.onrender.com";


const AMOUNT_SHORTCUTS = [
  10,
  30,
  50,
  75,
  100,
  150,
  200,
  250,
  300,
  500,
  750,
  1000,
  1500,
  2000,
  5000,
];

/*
|--------------------------------------------------------------------------
| DEPOSIT METHODS
|--------------------------------------------------------------------------
*/

const DEPOSIT_METHODS = [
  {
    id: "TRC20",
    name: "Tether (USDT)",
    network: "TRC-20",
    coin: "USDT",
    field: "trc20Address",
    walletKey: "TRC20",
    min: 10,
  },
  {
    id: "TRON_TRX",
    name: "Tron",
    network: "TRON",
    coin: "TRX",
    field: "trxAddress",
    walletKey: "TRON_TRX",
    min: 10,
  },
  {
    id: "ERC20",
    name: "Ethereum",
    network: "ERC-20",
    coin: "ETH",
    field: "erc20Address",
    walletKey: "ERC20",
    min: 10,
  },
  {
    id: "BNB_BEP20",
    name: "BNB Smart Chain",
    network: "BEP20 BSC",
    coin: "BNB",
    field: "bep20Address",
    walletKey: "BNB_BEP20",
    min: 10,
  },
  {
    id: "BITCOIN",
    name: "Bitcoin",
    network: "BTC",
    coin: "BTC",
    field: "bitcoinAddress",
    walletKey: "BITCOIN",
    min: 10,
  },
  {
    id: "SOLANA",
    name: "Solana",
    network: "SOLANA",
    coin: "SOL",
    field: "solanaAddress",
    walletKey: "SOLANA",
    min: 10,
  },
];

const WALLET_TRANSLATIONS = {
  "en": {
    "loadingWallet": "Loading wallet...",
    "notLoggedIn": "You are not logged in.",
    "userNotFound": "User account was not found.",
    "failedLoad": "Failed to load your account.",
    "walletMissing": "User document could not be found.",
    "walletFailed": "Wallet generation failed.",
    "addressMissing": "Wallet address is not ready.",
    "depositMethodMissing": "Deposit method is missing.",
    "minDeposit": "Minimum deposit",
    "copied": "✓ Copied",
    "copy": "Copy",
    "loading": "Loading wallet...",
    "cryptoDeposit": "CRYPTO DEPOSIT",
    "creatingWallet": "Creating wallet...",
    "pleaseWait": "Please wait",
    "walletNotCreated": "Wallet not created yet",
    "walletWillCreate": "Your {coin} deposit wallet will be created securely for your account.",
    "createWallet": "Create {coin} Wallet",
    "walletReady": "Wallet ready",
    "depositAmount": "Deposit amount",
    "enterAmount": "Enter Amount Min ${min}",
    "hideShortcuts": "Hide amount shortcuts",
    "showShortcuts": "Show all amount shortcuts",
    "continue": "Continue/confirmed",
    "scanDeposit": "Scan to deposit",
    "depositAddress": "Your {coin} deposit address",
    "sendOnly": "Send only {coin} on the {network} network to this address. Sending another coin or using another network may result in permanent loss.",
    "changeAmount": "← Change amount",
    "submitting": "Submitting...",
    "madeDeposit": "I Have Made The Deposit",
    "requestSubmitted": "Deposit request submitted. Waiting for confirmation.",
    "failedSubmit": "Failed to submit deposit request.",
    "specialOffer": "SPECIAL OFFER",
    "depositBonus": "Deposit & Get Bonus",
    "qualifyingDeposit": "Make a qualifying deposit and enjoy exclusive rewards on your account.",
    "viewOffers": "View Offers",
    "fastDeposits": "Fast Deposits",
    "secureCrypto": "Secure crypto deposits",
    "secureWallet": "Secure Wallet",
    "fundsProtected": "Your funds stay protected",
    "bonusProgram": "BONUS PROGRAM",
    "earnMore": "Earn More With Every Deposit",
    "chooseBonus": "Choose an eligible bonus and maximize your deposit rewards.",
    "firstDeposit": "FIRST DEPOSIT",
    "firstBonus": "First Deposit Bonus",
    "getBonus": "Get a bonus of",
    "firstQualifying": "on your first qualifying deposit.",
    "secondDeposit": "SECOND DEPOSIT",
    "secondBonus": "Second Deposit Bonus",
    "secondQualifying": "Make your second qualifying deposit and receive",
    "bonus": "bonus.",
    "everyWednesday": "EVERY WEDNESDAY",
    "weeklyBonus": "Weekly Wednesday Bonus",
    "wednesdayDeposit": "Deposit on Wednesday and enjoy a weekly bonus of",
    "walletCreated": "{coin} wallet created successfully.",
    "cannotConnect": "Cannot connect to wallet server. Make sure backend is running on {url}",
    "failedCreate": "Failed to create wallet.",
    "failedCopy": "Failed to copy wallet address. Please try again.",
    "minimumError": "Minimum deposit is ${min}.",
    "copyError": "Failed to copy wallet address. Please try again."
  },
  "sw": {
    "loadingWallet": "Inapakia pochi...",
    "notLoggedIn": "Hujaingia kwenye akaunti.",
    "userNotFound": "Akaunti ya mtumiaji haikupatikana.",
    "failedLoad": "Imeshindwa kupakia akaunti yako.",
    "walletMissing": "Taarifa za mtumiaji hazikupatikana.",
    "walletFailed": "Imeshindwa kuunda pochi.",
    "addressMissing": "Anwani ya pochi haiko tayari.",
    "depositMethodMissing": "Njia ya kuweka fedha haipo.",
    "minDeposit": "Kiwango cha chini cha kuweka fedha",
    "copied": "✓ Imenakiliwa",
    "copy": "Nakili",
    "cryptoDeposit": "WEKA CRYPTO",
    "creatingWallet": "Inaunda pochi...",
    "pleaseWait": "Tafadhali subiri",
    "walletNotCreated": "Pochi bado haijaundwa",
    "walletWillCreate": "Pochi yako ya {coin} itaundwa kwa usalama kwa akaunti yako.",
    "createWallet": "Unda Pochi ya {coin}",
    "walletReady": "Pochi iko tayari",
    "depositAmount": "Kiasi cha kuweka",
    "enterAmount": "Weka kiasi, kiwango cha chini ${min}",
    "hideShortcuts": "Ficha kiasi cha haraka",
    "showShortcuts": "Onyesha kiasi vyote vya haraka",
    "continue": "Endelea/Thibitisha",
    "scanDeposit": "Changanua kuweka fedha",
    "depositAddress": "Anwani yako ya kuweka {coin}",
    "sendOnly": "Tuma {coin} pekee kupitia mtandao wa {network} kwenda kwenye anwani hii. Kutuma sarafu nyingine au kutumia mtandao mwingine kunaweza kusababisha upotevu wa kudumu.",
    "changeAmount": "← Badilisha kiasi",
    "submitting": "Inatuma...",
    "madeDeposit": "Nimefanya malipo",
    "requestSubmitted": "Ombi la kuweka fedha limetumwa. Linasubiri uthibitisho.",
    "failedSubmit": "Imeshindwa kutuma ombi la kuweka fedha.",
    "specialOffer": "OFa MAALUM",
    "depositBonus": "Weka fedha upate bonasi",
    "qualifyingDeposit": "Weka kiasi kinachokubalika upate zawadi maalum kwenye akaunti yako.",
    "viewOffers": "Tazama ofa",
    "fastDeposits": "Kuweka fedha haraka",
    "secureCrypto": "Kuweka crypto kwa usalama",
    "secureWallet": "Pochi salama",
    "fundsProtected": "Fedha zako zinalindwa",
    "bonusProgram": "MPANGO WA BONASI",
    "earnMore": "Pata zaidi kwa kila uwekaji fedha",
    "chooseBonus": "Chagua bonasi inayokufaa ili kuongeza zawadi zako.",
    "firstDeposit": "UWEKAJI WA KWANZA",
    "firstBonus": "Bonasi ya uwekaji wa kwanza",
    "getBonus": "Pata bonasi ya",
    "firstQualifying": "kwenye uwekaji wako wa kwanza unaokubalika.",
    "secondDeposit": "UWEKAJI WA PILI",
    "secondBonus": "Bonasi ya uwekaji wa pili",
    "secondQualifying": "Weka fedha mara ya pili na upokee",
    "bonus": "bonasi.",
    "everyWednesday": "KILA JUMATANO",
    "weeklyBonus": "Bonasi ya kila Jumatano",
    "wednesdayDeposit": "Weka fedha Jumatano na upate bonasi ya kila wiki ya",
    "walletCreated": "Pochi ya {coin} imeundwa kwa mafanikio.",
    "cannotConnect": "Haiwezi kuunganishwa na seva ya pochi. Hakikisha backend inaendeshwa kwenye {url}",
    "failedCreate": "Imeshindwa kuunda pochi.",
    "failedCopy": "Imeshindwa kunakili anwani ya pochi. Jaribu tena.",
    "minimumError": "Kiasi cha chini cha kuweka fedha ni ${min}."
  },
  "fr": {
    "loadingWallet": "Chargement du portefeuille...",
    "notLoggedIn": "Vous n’êtes pas connecté.",
    "userNotFound": "Compte utilisateur introuvable.",
    "failedLoad": "Impossible de charger votre compte.",
    "addressMissing": "L’adresse du portefeuille n’est pas prête.",
    "walletReady": "Portefeuille prêt",
    "depositAmount": "Montant du dépôt",
    "continue": "Continuer / confirmer",
    "copy": "Copier",
    "copied": "✓ Copié",
    "creatingWallet": "Création du portefeuille...",
    "pleaseWait": "Veuillez patienter",
    "walletNotCreated": "Portefeuille pas encore créé",
    "createWallet": "Créer le portefeuille {coin}",
    "scanDeposit": "Scanner pour déposer",
    "changeAmount": "← Modifier le montant",
    "submitting": "Envoi...",
    "madeDeposit": "J’ai effectué le dépôt",
    "specialOffer": "OFFRE SPÉCIALE",
    "depositBonus": "Déposez et obtenez un bonus",
    "viewOffers": "Voir les offres",
    "fastDeposits": "Dépôts rapides",
    "secureWallet": "Portefeuille sécurisé",
    "bonusProgram": "PROGRAMME DE BONUS",
    "firstBonus": "Bonus de premier dépôt",
    "secondBonus": "Bonus de deuxième dépôt",
    "everyWednesday": "CHAQUE MERCREDI",
    "weeklyBonus": "Bonus du mercredi",
    "walletCreated": "Portefeuille {coin} créé avec succès.",
    "failedSubmit": "Échec de l’envoi de la demande de dépôt.",
    "minimumError": "Le dépôt minimum est de ${min}."
  },
  "es": {
    "loadingWallet": "Cargando billetera...",
    "notLoggedIn": "No has iniciado sesión.",
    "userNotFound": "No se encontró la cuenta del usuario.",
    "failedLoad": "No se pudo cargar tu cuenta.",
    "addressMissing": "La dirección de la billetera no está lista.",
    "walletReady": "Billetera lista",
    "depositAmount": "Importe del depósito",
    "continue": "Continuar/confirmar",
    "copy": "Copiar",
    "copied": "✓ Copiado",
    "creatingWallet": "Creando billetera...",
    "pleaseWait": "Espera, por favor",
    "walletNotCreated": "La billetera aún no se ha creado",
    "createWallet": "Crear billetera {coin}",
    "scanDeposit": "Escanea para depositar",
    "changeAmount": "← Cambiar importe",
    "submitting": "Enviando...",
    "madeDeposit": "He realizado el depósito",
    "specialOffer": "OFERTA ESPECIAL",
    "depositBonus": "Deposita y recibe un bono",
    "viewOffers": "Ver ofertas",
    "fastDeposits": "Depósitos rápidos",
    "secureWallet": "Billetera segura",
    "bonusProgram": "PROGRAMA DE BONOS",
    "firstBonus": "Bono del primer depósito",
    "secondBonus": "Bono del segundo depósito",
    "everyWednesday": "CADA MIÉRCOLES",
    "weeklyBonus": "Bono semanal del miércoles",
    "walletCreated": "Billetera {coin} creada correctamente.",
    "failedSubmit": "No se pudo enviar la solicitud de depósito.",
    "minimumError": "El depósito mínimo es ${min}."
  },
  "pt": {
    "loadingWallet": "Carregando carteira...",
    "notLoggedIn": "Você não está conectado.",
    "userNotFound": "Conta de usuário não encontrada.",
    "failedLoad": "Falha ao carregar sua conta.",
    "addressMissing": "O endereço da carteira não está pronto.",
    "walletReady": "Carteira pronta",
    "depositAmount": "Valor do depósito",
    "continue": "Continuar/confirmar",
    "copy": "Copiar",
    "copied": "✓ Copiado",
    "creatingWallet": "Criando carteira...",
    "pleaseWait": "Aguarde",
    "walletNotCreated": "A carteira ainda não foi criada",
    "createWallet": "Criar carteira {coin}",
    "scanDeposit": "Digitalize para depositar",
    "changeAmount": "← Alterar valor",
    "submitting": "Enviando...",
    "madeDeposit": "Já fiz o depósito",
    "specialOffer": "OFERTA ESPECIAL",
    "depositBonus": "Deposite e ganhe bônus",
    "viewOffers": "Ver ofertas",
    "fastDeposits": "Depósitos rápidos",
    "secureWallet": "Carteira segura",
    "bonusProgram": "PROGRAMA DE BÔNUS",
    "firstBonus": "Bônus do primeiro depósito",
    "secondBonus": "Bônus do segundo depósito",
    "everyWednesday": "TODAS AS QUARTAS-FEIRAS",
    "weeklyBonus": "Bônus semanal de quarta-feira",
    "walletCreated": "Carteira {coin} criada com sucesso.",
    "failedSubmit": "Falha ao enviar a solicitação de depósito.",
    "minimumError": "O depósito mínimo é ${min}."
  },
  "de": {
    "loadingWallet": "Wallet wird geladen...",
    "notLoggedIn": "Du bist nicht angemeldet.",
    "userNotFound": "Benutzerkonto wurde nicht gefunden.",
    "failedLoad": "Dein Konto konnte nicht geladen werden.",
    "addressMissing": "Wallet-Adresse ist noch nicht bereit.",
    "walletReady": "Wallet bereit",
    "depositAmount": "Einzahlungsbetrag",
    "continue": "Weiter/bestätigen",
    "copy": "Kopieren",
    "copied": "✓ Kopiert",
    "creatingWallet": "Wallet wird erstellt...",
    "pleaseWait": "Bitte warten",
    "walletNotCreated": "Wallet wurde noch nicht erstellt",
    "createWallet": "{coin}-Wallet erstellen",
    "scanDeposit": "Zum Einzahlen scannen",
    "changeAmount": "← Betrag ändern",
    "submitting": "Wird gesendet...",
    "madeDeposit": "Ich habe die Einzahlung vorgenommen",
    "specialOffer": "SONDERANGEBOT",
    "depositBonus": "Einzahlen und Bonus erhalten",
    "viewOffers": "Angebote ansehen",
    "fastDeposits": "Schnelle Einzahlungen",
    "secureWallet": "Sichere Wallet",
    "bonusProgram": "BONUSPROGRAMM",
    "firstBonus": "Bonus für die erste Einzahlung",
    "secondBonus": "Bonus für die zweite Einzahlung",
    "everyWednesday": "JEDEN MITTWOCH",
    "weeklyBonus": "Wöchentlicher Mittwoch-Bonus",
    "walletCreated": "{coin}-Wallet erfolgreich erstellt.",
    "failedSubmit": "Einzahlungsanfrage konnte nicht gesendet werden.",
    "minimumError": "Mindesteinzahlung beträgt ${min}."
  },
  "it": {
    "loadingWallet": "Caricamento portafoglio...",
    "notLoggedIn": "Non hai effettuato l’accesso.",
    "userNotFound": "Account utente non trovato.",
    "failedLoad": "Impossibile caricare il tuo account.",
    "addressMissing": "L’indirizzo del portafoglio non è pronto.",
    "walletReady": "Portafoglio pronto",
    "depositAmount": "Importo del deposito",
    "continue": "Continua/conferma",
    "copy": "Copia",
    "copied": "✓ Copiato",
    "creatingWallet": "Creazione portafoglio...",
    "pleaseWait": "Attendi",
    "walletNotCreated": "Portafoglio non ancora creato",
    "createWallet": "Crea portafoglio {coin}",
    "scanDeposit": "Scansiona per depositare",
    "changeAmount": "← Modifica importo",
    "submitting": "Invio...",
    "madeDeposit": "Ho effettuato il deposito",
    "specialOffer": "OFFERTA SPECIALE",
    "depositBonus": "Deposita e ricevi un bonus",
    "viewOffers": "Visualizza offerte",
    "fastDeposits": "Depositi rapidi",
    "secureWallet": "Portafoglio sicuro",
    "bonusProgram": "PROGRAMMA BONUS",
    "firstBonus": "Bonus primo deposito",
    "secondBonus": "Bonus secondo deposito",
    "everyWednesday": "OGNI MERCOLEDÌ",
    "weeklyBonus": "Bonus settimanale del mercoledì",
    "walletCreated": "Portafoglio {coin} creato con successo.",
    "failedSubmit": "Invio della richiesta di deposito non riuscito.",
    "minimumError": "Il deposito minimo è ${min}."
  },
  "nl": {
    "loadingWallet": "Wallet laden...",
    "notLoggedIn": "Je bent niet ingelogd.",
    "userNotFound": "Gebruikersaccount niet gevonden.",
    "failedLoad": "Je account kon niet worden geladen.",
    "addressMissing": "Walletadres is nog niet klaar.",
    "walletReady": "Wallet gereed",
    "depositAmount": "Stortingsbedrag",
    "continue": "Doorgaan/bevestigen",
    "copy": "Kopiëren",
    "copied": "✓ Gekopieerd",
    "creatingWallet": "Wallet aanmaken...",
    "pleaseWait": "Even geduld",
    "walletNotCreated": "Wallet is nog niet aangemaakt",
    "createWallet": "{coin}-wallet aanmaken",
    "scanDeposit": "Scan om te storten",
    "changeAmount": "← Bedrag wijzigen",
    "submitting": "Verzenden...",
    "madeDeposit": "Ik heb de storting gedaan",
    "specialOffer": "SPECIALE AANBIEDING",
    "depositBonus": "Stort en ontvang bonus",
    "viewOffers": "Aanbiedingen bekijken",
    "fastDeposits": "Snelle stortingen",
    "secureWallet": "Veilige wallet",
    "bonusProgram": "BONUSPROGRAMMA",
    "firstBonus": "Bonus voor eerste storting",
    "secondBonus": "Bonus voor tweede storting",
    "everyWednesday": "ELKE WOENSDAG",
    "weeklyBonus": "Wekelijkse woensdagbonus",
    "walletCreated": "{coin}-wallet succesvol aangemaakt.",
    "failedSubmit": "Stortingsverzoek verzenden mislukt.",
    "minimumError": "Minimale storting is ${min}."
  },
  "ar": {
    "loadingWallet": "جارٍ تحميل المحفظة...",
    "notLoggedIn": "لم تسجّل الدخول.",
    "userNotFound": "لم يتم العثور على حساب المستخدم.",
    "failedLoad": "تعذّر تحميل حسابك.",
    "addressMissing": "عنوان المحفظة غير جاهز.",
    "walletReady": "المحفظة جاهزة",
    "depositAmount": "مبلغ الإيداع",
    "continue": "متابعة/تأكيد",
    "copy": "نسخ",
    "copied": "✓ تم النسخ",
    "creatingWallet": "جارٍ إنشاء المحفظة...",
    "pleaseWait": "يرجى الانتظار",
    "walletNotCreated": "لم يتم إنشاء المحفظة بعد",
    "createWallet": "إنشاء محفظة {coin}",
    "scanDeposit": "امسح للإيداع",
    "changeAmount": "← تغيير المبلغ",
    "submitting": "جارٍ الإرسال...",
    "madeDeposit": "لقد أجريت الإيداع",
    "specialOffer": "عرض خاص",
    "depositBonus": "أودع واحصل على مكافأة",
    "viewOffers": "عرض العروض",
    "fastDeposits": "إيداعات سريعة",
    "secureWallet": "محفظة آمنة",
    "bonusProgram": "برنامج المكافآت",
    "firstBonus": "مكافأة الإيداع الأول",
    "secondBonus": "مكافأة الإيداع الثاني",
    "everyWednesday": "كل أربعاء",
    "weeklyBonus": "مكافأة الأربعاء الأسبوعية",
    "walletCreated": "تم إنشاء محفظة {coin} بنجاح.",
    "failedSubmit": "فشل إرسال طلب الإيداع.",
    "minimumError": "الحد الأدنى للإيداع هو ${min}."
  },
  "zh": {
    "loadingWallet": "正在加载钱包...",
    "notLoggedIn": "您尚未登录。",
    "userNotFound": "未找到用户账户。",
    "failedLoad": "无法加载您的账户。",
    "addressMissing": "钱包地址尚未准备好。",
    "walletReady": "钱包已就绪",
    "depositAmount": "充值金额",
    "continue": "继续/确认",
    "copy": "复制",
    "copied": "✓ 已复制",
    "creatingWallet": "正在创建钱包...",
    "pleaseWait": "请稍候",
    "walletNotCreated": "尚未创建钱包",
    "createWallet": "创建 {coin} 钱包",
    "scanDeposit": "扫码充值",
    "changeAmount": "← 修改金额",
    "submitting": "正在提交...",
    "madeDeposit": "我已完成充值",
    "specialOffer": "特别优惠",
    "depositBonus": "充值并获得奖励",
    "viewOffers": "查看优惠",
    "fastDeposits": "快速充值",
    "secureWallet": "安全钱包",
    "bonusProgram": "奖励计划",
    "firstBonus": "首次充值奖励",
    "secondBonus": "第二次充值奖励",
    "everyWednesday": "每周三",
    "weeklyBonus": "周三奖励",
    "walletCreated": "{coin} 钱包创建成功。",
    "failedSubmit": "提交充值请求失败。",
    "minimumError": "最低充值金额为 ${min}。"
  },
  "hi": {
    "loadingWallet": "वॉलेट लोड हो रहा है...",
    "notLoggedIn": "आप लॉग इन नहीं हैं।",
    "userNotFound": "उपयोगकर्ता खाता नहीं मिला।",
    "failedLoad": "आपका खाता लोड नहीं हो सका।",
    "addressMissing": "वॉलेट पता तैयार नहीं है।",
    "walletReady": "वॉलेट तैयार है",
    "depositAmount": "जमा राशि",
    "continue": "जारी रखें/पुष्टि करें",
    "copy": "कॉपी करें",
    "copied": "✓ कॉपी किया गया",
    "creatingWallet": "वॉलेट बनाया जा रहा है...",
    "pleaseWait": "कृपया प्रतीक्षा करें",
    "walletNotCreated": "वॉलेट अभी नहीं बनाया गया है",
    "createWallet": "{coin} वॉलेट बनाएं",
    "scanDeposit": "जमा करने के लिए स्कैन करें",
    "changeAmount": "← राशि बदलें",
    "submitting": "जमा किया जा रहा है...",
    "madeDeposit": "मैंने जमा कर दिया है",
    "specialOffer": "विशेष ऑफ़र",
    "depositBonus": "जमा करें और बोनस पाएं",
    "viewOffers": "ऑफ़र देखें",
    "fastDeposits": "तेज़ जमा",
    "secureWallet": "सुरक्षित वॉलेट",
    "bonusProgram": "बोनस कार्यक्रम",
    "firstBonus": "पहली जमा का बोनस",
    "secondBonus": "दूसरी जमा का बोनस",
    "everyWednesday": "हर बुधवार",
    "weeklyBonus": "साप्ताहिक बुधवार बोनस",
    "walletCreated": "{coin} वॉलेट सफलतापूर्वक बनाया गया।",
    "failedSubmit": "जमा अनुरोध भेजने में विफल।",
    "minimumError": "न्यूनतम जमा ${min} है।"
  },
  "ru": {
    "loadingWallet": "Загрузка кошелька...",
    "notLoggedIn": "Вы не вошли в систему.",
    "userNotFound": "Аккаунт пользователя не найден.",
    "failedLoad": "Не удалось загрузить аккаунт.",
    "addressMissing": "Адрес кошелька ещё не готов.",
    "walletReady": "Кошелёк готов",
    "depositAmount": "Сумма депозита",
    "continue": "Продолжить/подтвердить",
    "copy": "Копировать",
    "copied": "✓ Скопировано",
    "creatingWallet": "Создание кошелька...",
    "pleaseWait": "Пожалуйста, подождите",
    "walletNotCreated": "Кошелёк ещё не создан",
    "createWallet": "Создать кошелёк {coin}",
    "scanDeposit": "Сканируйте для депозита",
    "changeAmount": "← Изменить сумму",
    "submitting": "Отправка...",
    "madeDeposit": "Я внес депозит",
    "specialOffer": "СПЕЦИАЛЬНОЕ ПРЕДЛОЖЕНИЕ",
    "depositBonus": "Пополните счёт и получите бонус",
    "viewOffers": "Посмотреть предложения",
    "fastDeposits": "Быстрые депозиты",
    "secureWallet": "Безопасный кошелёк",
    "bonusProgram": "БОНУСНАЯ ПРОГРАММА",
    "firstBonus": "Бонус за первый депозит",
    "secondBonus": "Бонус за второй депозит",
    "everyWednesday": "КАЖДУЮ СРЕДУ",
    "weeklyBonus": "Еженедельный бонус по средам",
    "walletCreated": "Кошелёк {coin} успешно создан.",
    "failedSubmit": "Не удалось отправить запрос на депозит.",
    "minimumError": "Минимальный депозит — ${min}."
  },
  "ja": {
    "loadingWallet": "ウォレットを読み込み中...",
    "notLoggedIn": "ログインしていません。",
    "userNotFound": "ユーザーアカウントが見つかりません。",
    "failedLoad": "アカウントを読み込めませんでした。",
    "addressMissing": "ウォレットアドレスはまだ準備できていません。",
    "walletReady": "ウォレット準備完了",
    "depositAmount": "入金額",
    "continue": "続行／確認",
    "copy": "コピー",
    "copied": "✓ コピーしました",
    "creatingWallet": "ウォレットを作成中...",
    "pleaseWait": "お待ちください",
    "walletNotCreated": "ウォレットはまだ作成されていません",
    "createWallet": "{coin}ウォレットを作成",
    "scanDeposit": "スキャンして入金",
    "changeAmount": "← 金額を変更",
    "submitting": "送信中...",
    "madeDeposit": "入金しました",
    "specialOffer": "特別オファー",
    "depositBonus": "入金してボーナスを獲得",
    "viewOffers": "オファーを見る",
    "fastDeposits": "高速入金",
    "secureWallet": "安全なウォレット",
    "bonusProgram": "ボーナスプログラム",
    "firstBonus": "初回入金ボーナス",
    "secondBonus": "2回目の入金ボーナス",
    "everyWednesday": "毎週水曜日",
    "weeklyBonus": "水曜日の週間ボーナス",
    "walletCreated": "{coin}ウォレットを作成しました。",
    "failedSubmit": "入金リクエストの送信に失敗しました。",
    "minimumError": "最低入金額は ${min} です。"
  },
  "ko": {
    "loadingWallet": "지갑을 불러오는 중...",
    "notLoggedIn": "로그인하지 않았습니다.",
    "userNotFound": "사용자 계정을 찾을 수 없습니다.",
    "failedLoad": "계정을 불러오지 못했습니다.",
    "addressMissing": "지갑 주소가 아직 준비되지 않았습니다.",
    "walletReady": "지갑 준비 완료",
    "depositAmount": "입금 금액",
    "continue": "계속/확인",
    "copy": "복사",
    "copied": "✓ 복사됨",
    "creatingWallet": "지갑 생성 중...",
    "pleaseWait": "잠시 기다려 주세요",
    "walletNotCreated": "지갑이 아직 생성되지 않았습니다",
    "createWallet": "{coin} 지갑 만들기",
    "scanDeposit": "스캔하여 입금",
    "changeAmount": "← 금액 변경",
    "submitting": "제출 중...",
    "madeDeposit": "입금을 완료했습니다",
    "specialOffer": "특별 혜택",
    "depositBonus": "입금하고 보너스 받기",
    "viewOffers": "혜택 보기",
    "fastDeposits": "빠른 입금",
    "secureWallet": "안전한 지갑",
    "bonusProgram": "보너스 프로그램",
    "firstBonus": "첫 입금 보너스",
    "secondBonus": "두 번째 입금 보너스",
    "everyWednesday": "매주 수요일",
    "weeklyBonus": "수요일 주간 보너스",
    "walletCreated": "{coin} 지갑이 성공적으로 생성되었습니다.",
    "failedSubmit": "입금 요청 제출에 실패했습니다.",
    "minimumError": "최소 입금액은 ${min}입니다."
  },
  "id": {
    "loadingWallet": "Memuat dompet...",
    "notLoggedIn": "Anda belum masuk.",
    "userNotFound": "Akun pengguna tidak ditemukan.",
    "failedLoad": "Gagal memuat akun Anda.",
    "addressMissing": "Alamat dompet belum siap.",
    "walletReady": "Dompet siap",
    "depositAmount": "Jumlah deposit",
    "continue": "Lanjutkan/konfirmasi",
    "copy": "Salin",
    "copied": "✓ Disalin",
    "creatingWallet": "Membuat dompet...",
    "pleaseWait": "Harap tunggu",
    "walletNotCreated": "Dompet belum dibuat",
    "createWallet": "Buat Dompet {coin}",
    "scanDeposit": "Pindai untuk deposit",
    "changeAmount": "← Ubah jumlah",
    "submitting": "Mengirim...",
    "madeDeposit": "Saya sudah melakukan deposit",
    "specialOffer": "PENAWARAN KHUSUS",
    "depositBonus": "Deposit dan dapatkan bonus",
    "viewOffers": "Lihat penawaran",
    "fastDeposits": "Deposit cepat",
    "secureWallet": "Dompet aman",
    "bonusProgram": "PROGRAM BONUS",
    "firstBonus": "Bonus deposit pertama",
    "secondBonus": "Bonus deposit kedua",
    "everyWednesday": "SETIAP RABU",
    "weeklyBonus": "Bonus Rabu mingguan",
    "walletCreated": "Dompet {coin} berhasil dibuat.",
    "failedSubmit": "Gagal mengirim permintaan deposit.",
    "minimumError": "Deposit minimum adalah ${min}."
  },
  "tr": {
    "loadingWallet": "Cüzdan yükleniyor...",
    "notLoggedIn": "Giriş yapmadınız.",
    "userNotFound": "Kullanıcı hesabı bulunamadı.",
    "failedLoad": "Hesabınız yüklenemedi.",
    "addressMissing": "Cüzdan adresi hazır değil.",
    "walletReady": "Cüzdan hazır",
    "depositAmount": "Yatırma tutarı",
    "continue": "Devam et/onayla",
    "copy": "Kopyala",
    "copied": "✓ Kopyalandı",
    "creatingWallet": "Cüzdan oluşturuluyor...",
    "pleaseWait": "Lütfen bekleyin",
    "walletNotCreated": "Cüzdan henüz oluşturulmadı",
    "createWallet": "{coin} Cüzdanı Oluştur",
    "scanDeposit": "Yatırmak için tara",
    "changeAmount": "← Tutarı değiştir",
    "submitting": "Gönderiliyor...",
    "madeDeposit": "Yatırma işlemini yaptım",
    "specialOffer": "ÖZEL TEKLİF",
    "depositBonus": "Yatır ve bonus kazan",
    "viewOffers": "Teklifleri görüntüle",
    "fastDeposits": "Hızlı yatırma",
    "secureWallet": "Güvenli cüzdan",
    "bonusProgram": "BONUS PROGRAMI",
    "firstBonus": "İlk yatırma bonusu",
    "secondBonus": "İkinci yatırma bonusu",
    "everyWednesday": "HER ÇARŞAMBA",
    "weeklyBonus": "Haftalık Çarşamba bonusu",
    "walletCreated": "{coin} cüzdanı başarıyla oluşturuldu.",
    "failedSubmit": "Yatırma isteği gönderilemedi.",
    "minimumError": "Minimum yatırma tutarı ${min}."
  },
  "vi": {
    "loadingWallet": "Đang tải ví...",
    "notLoggedIn": "Bạn chưa đăng nhập.",
    "userNotFound": "Không tìm thấy tài khoản người dùng.",
    "failedLoad": "Không thể tải tài khoản của bạn.",
    "addressMissing": "Địa chỉ ví chưa sẵn sàng.",
    "walletReady": "Ví đã sẵn sàng",
    "depositAmount": "Số tiền nạp",
    "continue": "Tiếp tục/xác nhận",
    "copy": "Sao chép",
    "copied": "✓ Đã sao chép",
    "creatingWallet": "Đang tạo ví...",
    "pleaseWait": "Vui lòng chờ",
    "walletNotCreated": "Ví chưa được tạo",
    "createWallet": "Tạo ví {coin}",
    "scanDeposit": "Quét để nạp tiền",
    "changeAmount": "← Thay đổi số tiền",
    "submitting": "Đang gửi...",
    "madeDeposit": "Tôi đã nạp tiền",
    "specialOffer": "ƯU ĐÃI ĐẶC BIỆT",
    "depositBonus": "Nạp tiền và nhận thưởng",
    "viewOffers": "Xem ưu đãi",
    "fastDeposits": "Nạp tiền nhanh",
    "secureWallet": "Ví an toàn",
    "bonusProgram": "CHƯƠNG TRÌNH THƯỞNG",
    "firstBonus": "Thưởng nạp lần đầu",
    "secondBonus": "Thưởng nạp lần hai",
    "everyWednesday": "MỖI THỨ TƯ",
    "weeklyBonus": "Thưởng thứ Tư hàng tuần",
    "walletCreated": "Đã tạo ví {coin} thành công.",
    "failedSubmit": "Gửi yêu cầu nạp tiền thất bại.",
    "minimumError": "Số tiền nạp tối thiểu là ${min}."
  },
  "th": {
    "loadingWallet": "กำลังโหลดกระเป๋า...",
    "notLoggedIn": "คุณยังไม่ได้เข้าสู่ระบบ",
    "userNotFound": "ไม่พบบัญชีผู้ใช้",
    "failedLoad": "โหลดบัญชีของคุณไม่สำเร็จ",
    "addressMissing": "ที่อยู่กระเป๋ายังไม่พร้อม",
    "walletReady": "กระเป๋าพร้อมแล้ว",
    "depositAmount": "จำนวนเงินฝาก",
    "continue": "ดำเนินการต่อ/ยืนยัน",
    "copy": "คัดลอก",
    "copied": "✓ คัดลอกแล้ว",
    "creatingWallet": "กำลังสร้างกระเป๋า...",
    "pleaseWait": "โปรดรอสักครู่",
    "walletNotCreated": "ยังไม่ได้สร้างกระเป๋า",
    "createWallet": "สร้างกระเป๋า {coin}",
    "scanDeposit": "สแกนเพื่อฝาก",
    "changeAmount": "← เปลี่ยนจำนวนเงิน",
    "submitting": "กำลังส่ง...",
    "madeDeposit": "ฉันฝากเงินแล้ว",
    "specialOffer": "ข้อเสนอพิเศษ",
    "depositBonus": "ฝากเงินและรับโบนัส",
    "viewOffers": "ดูข้อเสนอ",
    "fastDeposits": "ฝากเงินรวดเร็ว",
    "secureWallet": "กระเป๋าปลอดภัย",
    "bonusProgram": "โปรแกรมโบนัส",
    "firstBonus": "โบนัสฝากครั้งแรก",
    "secondBonus": "โบนัสฝากครั้งที่สอง",
    "everyWednesday": "ทุกวันพุธ",
    "weeklyBonus": "โบนัสวันพุธรายสัปดาห์",
    "walletCreated": "สร้างกระเป๋า {coin} สำเร็จ",
    "failedSubmit": "ส่งคำขอฝากเงินไม่สำเร็จ",
    "minimumError": "ยอดฝากขั้นต่ำคือ ${min}"
  },
  "ms": {
    "loadingWallet": "Memuatkan dompet...",
    "notLoggedIn": "Anda belum log masuk.",
    "userNotFound": "Akaun pengguna tidak ditemui.",
    "failedLoad": "Gagal memuatkan akaun anda.",
    "addressMissing": "Alamat dompet belum tersedia.",
    "walletReady": "Dompet sedia",
    "depositAmount": "Jumlah deposit",
    "continue": "Teruskan/sahkan",
    "copy": "Salin",
    "copied": "✓ Disalin",
    "creatingWallet": "Mencipta dompet...",
    "pleaseWait": "Sila tunggu",
    "walletNotCreated": "Dompet belum dicipta",
    "createWallet": "Cipta Dompet {coin}",
    "scanDeposit": "Imbas untuk deposit",
    "changeAmount": "← Tukar jumlah",
    "submitting": "Menghantar...",
    "madeDeposit": "Saya telah membuat deposit",
    "specialOffer": "TAWARAN KHAS",
    "depositBonus": "Deposit dan dapatkan bonus",
    "viewOffers": "Lihat tawaran",
    "fastDeposits": "Deposit pantas",
    "secureWallet": "Dompet selamat",
    "bonusProgram": "PROGRAM BONUS",
    "firstBonus": "Bonus deposit pertama",
    "secondBonus": "Bonus deposit kedua",
    "everyWednesday": "SETIAP RABU",
    "weeklyBonus": "Bonus Rabu mingguan",
    "walletCreated": "Dompet {coin} berjaya dicipta.",
    "failedSubmit": "Gagal menghantar permintaan deposit.",
    "minimumError": "Deposit minimum ialah ${min}."
  },
  "ur": {
    "loadingWallet": "والیٹ لوڈ ہو رہا ہے...",
    "notLoggedIn": "آپ لاگ اِن نہیں ہیں۔",
    "userNotFound": "صارف کا اکاؤنٹ نہیں ملا۔",
    "failedLoad": "آپ کا اکاؤنٹ لوڈ نہیں ہو سکا۔",
    "addressMissing": "والیٹ کا پتہ ابھی تیار نہیں۔",
    "walletReady": "والیٹ تیار ہے",
    "depositAmount": "جمع کرانے کی رقم",
    "continue": "جاری رکھیں/تصدیق کریں",
    "copy": "کاپی کریں",
    "copied": "✓ کاپی ہو گیا",
    "creatingWallet": "والیٹ بنایا جا رہا ہے...",
    "pleaseWait": "براہ کرم انتظار کریں",
    "walletNotCreated": "والیٹ ابھی نہیں بنایا گیا",
    "createWallet": "{coin} والیٹ بنائیں",
    "scanDeposit": "جمع کرنے کے لیے اسکین کریں",
    "changeAmount": "← رقم تبدیل کریں",
    "submitting": "جمع ہو رہا ہے...",
    "madeDeposit": "میں نے رقم جمع کر دی ہے",
    "specialOffer": "خصوصی پیشکش",
    "depositBonus": "رقم جمع کریں اور بونس پائیں",
    "viewOffers": "پیشکشیں دیکھیں",
    "fastDeposits": "تیز جمع",
    "secureWallet": "محفوظ والیٹ",
    "bonusProgram": "بونس پروگرام",
    "firstBonus": "پہلی جمع کا بونس",
    "secondBonus": "دوسری جمع کا بونس",
    "everyWednesday": "ہر بدھ",
    "weeklyBonus": "ہفتہ وار بدھ بونس",
    "walletCreated": "{coin} والیٹ کامیابی سے بن گیا۔",
    "failedSubmit": "جمع کی درخواست بھیجنے میں ناکامی۔",
    "minimumError": "کم از کم جمع ${min} ہے۔"
  },
  "fa": {
    "loadingWallet": "در حال بارگذاری کیف پول...",
    "notLoggedIn": "وارد حساب نشده‌اید.",
    "userNotFound": "حساب کاربری پیدا نشد.",
    "failedLoad": "بارگذاری حساب شما ناموفق بود.",
    "addressMissing": "نشانی کیف پول آماده نیست.",
    "walletReady": "کیف پول آماده است",
    "depositAmount": "مبلغ واریز",
    "continue": "ادامه/تأیید",
    "copy": "کپی",
    "copied": "✓ کپی شد",
    "creatingWallet": "در حال ساخت کیف پول...",
    "pleaseWait": "لطفاً صبر کنید",
    "walletNotCreated": "کیف پول هنوز ساخته نشده است",
    "createWallet": "ساخت کیف پول {coin}",
    "scanDeposit": "برای واریز اسکن کنید",
    "changeAmount": "← تغییر مبلغ",
    "submitting": "در حال ارسال...",
    "madeDeposit": "واریز را انجام داده‌ام",
    "specialOffer": "پیشنهاد ویژه",
    "depositBonus": "واریز کنید و پاداش بگیرید",
    "viewOffers": "مشاهده پیشنهادها",
    "fastDeposits": "واریز سریع",
    "secureWallet": "کیف پول امن",
    "bonusProgram": "برنامه پاداش",
    "firstBonus": "پاداش اولین واریز",
    "secondBonus": "پاداش دومین واریز",
    "everyWednesday": "هر چهارشنبه",
    "weeklyBonus": "پاداش هفتگی چهارشنبه",
    "walletCreated": "کیف پول {coin} با موفقیت ساخته شد.",
    "failedSubmit": "ارسال درخواست واریز ناموفق بود.",
    "minimumError": "حداقل واریز ${min} است."
  },
  "bn": {
    "loadingWallet": "ওয়ালেট লোড হচ্ছে...",
    "notLoggedIn": "আপনি লগ ইন করেননি।",
    "userNotFound": "ব্যবহারকারীর অ্যাকাউন্ট পাওয়া যায়নি।",
    "failedLoad": "আপনার অ্যাকাউন্ট লোড করা যায়নি।",
    "addressMissing": "ওয়ালেট ঠিকানা প্রস্তুত নয়।",
    "walletReady": "ওয়ালেট প্রস্তুত",
    "depositAmount": "জমার পরিমাণ",
    "continue": "চালিয়ে যান/নিশ্চিত করুন",
    "copy": "কপি",
    "copied": "✓ কপি হয়েছে",
    "creatingWallet": "ওয়ালেট তৈরি হচ্ছে...",
    "pleaseWait": "অনুগ্রহ করে অপেক্ষা করুন",
    "walletNotCreated": "ওয়ালেট এখনও তৈরি হয়নি",
    "createWallet": "{coin} ওয়ালেট তৈরি করুন",
    "scanDeposit": "জমা দিতে স্ক্যান করুন",
    "changeAmount": "← পরিমাণ পরিবর্তন",
    "submitting": "জমা দেওয়া হচ্ছে...",
    "madeDeposit": "আমি জমা দিয়েছি",
    "specialOffer": "বিশেষ অফার",
    "depositBonus": "জমা দিন এবং বোনাস পান",
    "viewOffers": "অফার দেখুন",
    "fastDeposits": "দ্রুত জমা",
    "secureWallet": "নিরাপদ ওয়ালেট",
    "bonusProgram": "বোনাস প্রোগ্রাম",
    "firstBonus": "প্রথম জমার বোনাস",
    "secondBonus": "দ্বিতীয় জমার বোনাস",
    "everyWednesday": "প্রতি বুধবার",
    "weeklyBonus": "সাপ্তাহিক বুধবার বোনাস",
    "walletCreated": "{coin} ওয়ালেট সফলভাবে তৈরি হয়েছে।",
    "failedSubmit": "জমার অনুরোধ পাঠানো যায়নি।",
    "minimumError": "সর্বনিম্ন জমা ${min}।"
  },
  "ha": {
    "loadingWallet": "Ana loda walat...",
    "notLoggedIn": "Ba ka shiga ba.",
    "userNotFound": "Ba a sami asusun mai amfani ba.",
    "failedLoad": "An kasa loda asusunka.",
    "addressMissing": "Adireshin walat bai shirya ba.",
    "walletReady": "Walat ya shirya",
    "depositAmount": "Adadin ajiya",
    "continue": "Ci gaba/tabbatar",
    "copy": "Kwafi",
    "copied": "✓ An kwafa",
    "creatingWallet": "Ana ƙirƙirar walat...",
    "pleaseWait": "Da fatan jira",
    "walletNotCreated": "Ba a ƙirƙiri walat ba tukuna",
    "createWallet": "Ƙirƙiri walat {coin}",
    "scanDeposit": "Duba don yin ajiya",
    "changeAmount": "← Canja adadi",
    "submitting": "Ana aikawa...",
    "madeDeposit": "Na yi ajiyar kuɗi",
    "specialOffer": "KYAKKYAWAR TAYI",
    "depositBonus": "Yi ajiya ka sami kari",
    "viewOffers": "Duba tayin",
    "fastDeposits": "Ajiya cikin sauri",
    "secureWallet": "Walat mai tsaro",
    "bonusProgram": "SHIRIN KARI",
    "firstBonus": "Karin ajiya ta farko",
    "secondBonus": "Karin ajiya ta biyu",
    "everyWednesday": "KOWACE LARABA",
    "weeklyBonus": "Karin Laraba na mako-mako",
    "walletCreated": "An ƙirƙiri walat {coin} cikin nasara.",
    "failedSubmit": "An kasa aika buƙatar ajiya.",
    "minimumError": "Mafi ƙarancin ajiya ${min} ne."
  },
  "yo": {
    "loadingWallet": "Ń ṣí àpò owó...",
    "notLoggedIn": "O kò tíì wọlé.",
    "userNotFound": "A kò rí àkọọ́lẹ̀ olùlò.",
    "failedLoad": "A kò lè ṣí àkọọ́lẹ̀ rẹ.",
    "addressMissing": "Àdírẹ́sì àpò owó kò tíì ṣetán.",
    "walletReady": "Àpò owó ti ṣetán",
    "depositAmount": "Iye owó ìfipamọ́",
    "continue": "Tẹ̀síwájú/jẹ́rìí",
    "copy": "Daakọ",
    "copied": "✓ Ti daakọ",
    "creatingWallet": "Ń ṣẹ̀dá àpò owó...",
    "pleaseWait": "Jọ̀wọ́ dúró",
    "walletNotCreated": "A kò tíì ṣẹ̀dá àpò owó",
    "createWallet": "Ṣẹ̀dá àpò {coin}",
    "scanDeposit": "Ṣàyẹ̀wò láti fi owó sí",
    "changeAmount": "← Yí iye padà",
    "submitting": "Ń fi ránṣẹ́...",
    "madeDeposit": "Mo ti fi owó sí",
    "specialOffer": "ÀFẸ́RẸ́ PÀTÀKÌ",
    "depositBonus": "Fi owó sí, gba ẹ̀bùn",
    "viewOffers": "Wo àwọn àfẹ́rẹ́",
    "fastDeposits": "Ìfipamọ́ kíákíá",
    "secureWallet": "Àpò owó tó ní ààbò",
    "bonusProgram": "ÈTÒ Ẹ̀BÙN",
    "firstBonus": "Ẹ̀bùn ìfipamọ́ àkọ́kọ́",
    "secondBonus": "Ẹ̀bùn ìfipamọ́ kejì",
    "everyWednesday": "GBOGBO ỌJỌ́RU",
    "weeklyBonus": "Ẹ̀bùn Ọjọ́rú ọ̀sẹ̀",
    "walletCreated": "A ṣẹ̀dá àpò {coin} dáadáa.",
    "failedSubmit": "Kò ṣeé fi ìbéèrè ìfipamọ́ ránṣẹ́.",
    "minimumError": "Ìfipamọ́ tó kéré jù ni ${min}."
  },
  "zu": {
    "loadingWallet": "Ilayisha isikhwama...",
    "notLoggedIn": "Awungenanga ngemvume.",
    "userNotFound": "I-akhawunti yomsebenzisi ayitholakalanga.",
    "failedLoad": "Yehlulekile ukulayisha i-akhawunti yakho.",
    "addressMissing": "Ikheli lesikhwama alikalungi.",
    "walletReady": "Isikhwama silungile",
    "depositAmount": "Inani lediphozithi",
    "continue": "Qhubeka/qinisekisa",
    "copy": "Kopisha",
    "copied": "✓ Kukopishiwe",
    "creatingWallet": "Kwakhiwa isikhwama...",
    "pleaseWait": "Sicela ulinde",
    "walletNotCreated": "Isikhwama asikakhiwa",
    "createWallet": "Dala isikhwama se-{coin}",
    "scanDeposit": "Skena ukuze ufake imali",
    "changeAmount": "← Shintsha inani",
    "submitting": "Kuyathunyelwa...",
    "madeDeposit": "Ngiyifakile imali",
    "specialOffer": "ISIPHO ESITHILE",
    "depositBonus": "Faka imali uthole ibhonasi",
    "viewOffers": "Buka izipho",
    "fastDeposits": "Amadiphozithi asheshayo",
    "secureWallet": "Isikhwama esivikelekile",
    "bonusProgram": "UHLELO LWAMABHONASI",
    "firstBonus": "Ibhonasi yediphozithi yokuqala",
    "secondBonus": "Ibhonasi yediphozithi yesibili",
    "everyWednesday": "NJALO NGOLWESITHATHU",
    "weeklyBonus": "Ibhonasi yamasonto onke yangoLwesithathu",
    "walletCreated": "Isikhwama se-{coin} sidalwe ngempumelelo.",
    "failedSubmit": "Kwehlulekile ukuthumela isicelo sediphozithi.",
    "minimumError": "Idiphozithi encane ngu-${min}."
  },
  "am": {
    "loadingWallet": "የኪስ ቦርሳ በመጫን ላይ...",
    "notLoggedIn": "ገብተው አልገቡም።",
    "userNotFound": "የተጠቃሚ መለያ አልተገኘም።",
    "failedLoad": "መለያዎን መጫን አልተቻለም።",
    "addressMissing": "የኪስ ቦርሳ አድራሻ ዝግጁ አይደለም።",
    "walletReady": "ኪስ ቦርሳው ዝግጁ ነው",
    "depositAmount": "የማስገቢያ መጠን",
    "continue": "ቀጥል/አረጋግጥ",
    "copy": "ቅዳ",
    "copied": "✓ ተቀድቷል",
    "creatingWallet": "ኪስ ቦርሳ በመፍጠር ላይ...",
    "pleaseWait": "እባክዎ ይጠብቁ",
    "walletNotCreated": "ኪስ ቦርሳው ገና አልተፈጠረም",
    "createWallet": "{coin} ኪስ ቦርሳ ፍጠር",
    "scanDeposit": "ለማስገባት ይቃኙ",
    "changeAmount": "← መጠኑን ቀይር",
    "submitting": "በመላክ ላይ...",
    "madeDeposit": "ገንዘብ አስገብቻለሁ",
    "specialOffer": "ልዩ ቅናሽ",
    "depositBonus": "ገንዘብ አስገባ ቦነስ አግኝ",
    "viewOffers": "ቅናሾችን ይመልከቱ",
    "fastDeposits": "ፈጣን ማስገቢያዎች",
    "secureWallet": "ደህንነቱ የተጠበቀ ኪስ ቦርሳ",
    "bonusProgram": "የቦነስ ፕሮግራም",
    "firstBonus": "የመጀመሪያ ማስገቢያ ቦነስ",
    "secondBonus": "የሁለተኛ ማስገቢያ ቦነስ",
    "everyWednesday": "በየረቡዕ",
    "weeklyBonus": "ሳምንታዊ የረቡዕ ቦነስ",
    "walletCreated": "{coin} ኪስ ቦርሳ በተሳካ ሁኔታ ተፈጥሯል።",
    "failedSubmit": "የማስገቢያ ጥያቄ መላክ አልተሳካም።",
    "minimumError": "ዝቅተኛው ማስገቢያ ${min} ነው።"
  },
  "so": {
    "loadingWallet": "Boorsada waa la rarayaa...",
    "notLoggedIn": "Ma aadan gelin.",
    "userNotFound": "Akoonka isticmaalaha lama helin.",
    "failedLoad": "Akoonkaaga lama rari karin.",
    "addressMissing": "Cinwaanka boorsadu diyaar ma aha.",
    "walletReady": "Boorsadu waa diyaar",
    "depositAmount": "Qadarka dhigaalka",
    "continue": "Sii wad/xaqiiji",
    "copy": "Nuqul",
    "copied": "✓ Waa la koobiyey",
    "creatingWallet": "Boorsada waa la abuurayaa...",
    "pleaseWait": "Fadlan sug",
    "walletNotCreated": "Boorsada weli lama abuurin",
    "createWallet": "Abuur boorsada {coin}",
    "scanDeposit": "Sawir si aad u dhigato",
    "changeAmount": "← Beddel qadarka",
    "submitting": "Waa la dirayaa...",
    "madeDeposit": "Waan dhigay lacagta",
    "specialOffer": "SOO JEEDIN GAAR AH",
    "depositBonus": "Dhig lacag oo hel gunno",
    "viewOffers": "Eeg soo jeedimaha",
    "fastDeposits": "Dhigasho degdeg ah",
    "secureWallet": "Boorso ammaan ah",
    "bonusProgram": "BARNAAMIJKA GUNNADA",
    "firstBonus": "Gunnada dhigaalka koowaad",
    "secondBonus": "Gunnada dhigaalka labaad",
    "everyWednesday": "ARBACO KASTA",
    "weeklyBonus": "Gunnada toddobaadlaha ah ee Arbacada",
    "walletCreated": "Boorsada {coin} si guul leh ayaa loo abuuray.",
    "failedSubmit": "Dirista codsiga dhigaalka way fashilantay.",
    "minimumError": "Dhigaalka ugu yar waa ${min}."
  },
  "rw": {
    "loadingWallet": "Urupapuro rw’ikotomoni rurimo gufunguka...",
    "notLoggedIn": "Ntiwinjiye muri konti.",
    "userNotFound": "Konti y’ukoresha ntiyabonetse.",
    "failedLoad": "Ntibyakunze gufungura konti yawe.",
    "addressMissing": "Aderesi y’ikotomoni ntirategurwa.",
    "walletReady": "Ikotomoni ryiteguye",
    "depositAmount": "Amafaranga yo kubitsa",
    "continue": "Komeza/emeza",
    "copy": "Koporora",
    "copied": "✓ Byakoporowe",
    "creatingWallet": "Ikotomoni ririmo kuremwa...",
    "pleaseWait": "Tegereza gato",
    "walletNotCreated": "Ikotomoni ntiriraremwa",
    "createWallet": "Kora ikotomoni rya {coin}",
    "scanDeposit": "Sikana kugira ngo ubikize",
    "changeAmount": "← Hindura amafaranga",
    "submitting": "Birimo koherezwa...",
    "madeDeposit": "Nashyizemo amafaranga",
    "specialOffer": "IGIHEMBE CYIHARIYE",
    "depositBonus": "Bika amafaranga ubone ibihembo",
    "viewOffers": "Reba ibihembo",
    "fastDeposits": "Kubitsa byihuse",
    "secureWallet": "Ikotomoni rifite umutekano",
    "bonusProgram": "GAHUNDA Y’IBIHEMBE",
    "firstBonus": "Igihembo cyo kubitsa bwa mbere",
    "secondBonus": "Igihembo cyo kubitsa bwa kabiri",
    "everyWednesday": "BURI WA GATATU",
    "weeklyBonus": "Igihembo cya buri wa Gatatu",
    "walletCreated": "Ikotomoni rya {coin} ryakozwe neza.",
    "failedSubmit": "Kohereza icyifuzo cyo kubitsa byanze.",
    "minimumError": "Amafaranga make yo kubitsa ni ${min}."
  },
  "lg": {
    "loadingWallet": "Ekitabo ky’ensimbi kirina okuggulibwa...",
    "notLoggedIn": "Toyinze mu akawunti.",
    "userNotFound": "Akawunti y’omukozesa tezuuliddwa.",
    "failedLoad": "Tekisobose kutikka akawunti yo.",
    "addressMissing": "Endagiriro y’ekitabo ky’ensimbi tenategekebwa.",
    "walletReady": "Ekitabo ky’ensimbi kiri tayari",
    "depositAmount": "Omuwendo gw’ensimbi eziteekebwa",
    "continue": "Weyongere/kkakasa",
    "copy": "Koppa",
    "copied": "✓ Kikoppeddwa",
    "creatingWallet": "Ekitabo ky’ensimbi kikolebwa...",
    "pleaseWait": "Nsaba olinde",
    "walletNotCreated": "Ekitabo ky’ensimbi tekinnakolebwa",
    "createWallet": "Kola ekitabo kya {coin}",
    "scanDeposit": "Sikaani okuteeka ensimbi",
    "changeAmount": "← Kyusa omuwendo",
    "submitting": "Kitumibwa...",
    "madeDeposit": "Nteekeddemu ensimbi",
    "specialOffer": "EKIRABO EKYENJAWULO",
    "depositBonus": "Teeka ensimbi ofune ekirabo",
    "viewOffers": "Laba ebirabo",
    "fastDeposits": "Okuteeka ensimbi okw’amangu",
    "secureWallet": "Ekitabo eky’obukuumi",
    "bonusProgram": "ENTEGEKA Y’EBIRABO",
    "firstBonus": "Ekirabo ky’okuteeka ensimbi omulundi ogusooka",
    "secondBonus": "Ekirabo ky’omulundi ogw’okubiri",
    "everyWednesday": "BULI LWA GATU",
    "weeklyBonus": "Ekirabo kya buli wiiki ku Lwokusatu",
    "walletCreated": "Ekitabo kya {coin} kikolebwa bulungi.",
    "failedSubmit": "Okutuma okusaba kw’ensimbi kulemereddwa.",
    "minimumError": "Ensimbi entono eziteekebwa ziri ${min}."
  },
  "af": {
    "loadingWallet": "Beursie laai...",
    "notLoggedIn": "Jy is nie aangemeld nie.",
    "userNotFound": "Gebruikersrekening nie gevind nie.",
    "failedLoad": "Kon nie jou rekening laai nie.",
    "addressMissing": "Beursie-adres is nie gereed nie.",
    "walletReady": "Beursie gereed",
    "depositAmount": "Deposito-bedrag",
    "continue": "Gaan voort/bevestig",
    "copy": "Kopieer",
    "copied": "✓ Gekopieer",
    "creatingWallet": "Beursie word geskep...",
    "pleaseWait": "Wag asseblief",
    "walletNotCreated": "Beursie is nog nie geskep nie",
    "createWallet": "Skep {coin}-beursie",
    "scanDeposit": "Skandeer om te deponeer",
    "changeAmount": "← Verander bedrag",
    "submitting": "Dien in...",
    "madeDeposit": "Ek het die deposito gemaak",
    "specialOffer": "SPESIALE AANBIEDING",
    "depositBonus": "Deponeer en kry bonus",
    "viewOffers": "Bekyk aanbiedinge",
    "fastDeposits": "Vinnige deposito's",
    "secureWallet": "Veilige beursie",
    "bonusProgram": "BONUSPROGRAM",
    "firstBonus": "Eerste deposito-bonus",
    "secondBonus": "Tweede deposito-bonus",
    "everyWednesday": "ELKE WOENSDAG",
    "weeklyBonus": "Weeklikse Woensdag-bonus",
    "walletCreated": "{coin}-beursie suksesvol geskep.",
    "failedSubmit": "Kon nie deposito-versoek indien nie.",
    "minimumError": "Minimum deposito is ${min}."
  }
};

/*
|--------------------------------------------------------------------------
| COIN LOGO
|--------------------------------------------------------------------------
*/

const CoinLogo = ({ type }) => {
  const common = {
    width: 42,
    height: 42,
    viewBox: "0 0 40 40",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
  };

  /*
  |--------------------------------------------------------------------------
  | USDT
  |--------------------------------------------------------------------------
  */

  if (type === "USDT") {
    return (
      <svg {...common}>
        <circle
          cx="20"
          cy="20"
          r="18"
          fill="#26A17B"
        />

        <path
          d="M10 10.5H30V15H23V27.5H17V15H10V10.5Z"
          fill="white"
        />

        <path
          d="M20 15C25 15 29 16 29 17.5C29 19 25 20 20 20C15 20 11 19 11 17.5C11 16 15 15 20 15Z"
          fill="#26A17B"
        />

        <path
          d="M20 20V29"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | TRX
  |--------------------------------------------------------------------------
  */

  if (type === "TRX") {
    return (
      <svg {...common}>
        <circle
          cx="20"
          cy="20"
          r="18"
          fill="#EC1C24"
        />

        <path
          d="M10 10L30 13L22 30L10 10Z"
          fill="white"
        />

        <path
          d="M10 10L22 18L30 13"
          stroke="#EC1C24"
          strokeWidth="1.5"
        />

        <path
          d="M22 18L22 30"
          stroke="#EC1C24"
          strokeWidth="1.5"
        />
      </svg>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | ETH
  |--------------------------------------------------------------------------
  */

  if (type === "ETH") {
    return (
      <svg {...common}>
        <circle
          cx="20"
          cy="20"
          r="18"
          fill="#627EEA"
        />

        <path
          d="M20 6L12.5 20L20 24.5L27.5 20L20 6Z"
          fill="white"
        />

        <path
          d="M20 25.8L12.5 21.5L20 34L27.5 21.5L20 25.8Z"
          fill="white"
          opacity="0.7"
        />
      </svg>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | BNB
  |--------------------------------------------------------------------------
  */

  if (type === "BNB") {
    return (
      <svg {...common}>
        <circle
          cx="20"
          cy="20"
          r="18"
          fill="#F3BA2F"
        />

        <path
          d="M20 8L24 12L20 16L16 12L20 8Z"
          fill="#111827"
        />

        <path
          d="M13 15L16 18L13 21L10 18L13 15Z"
          fill="#111827"
        />

        <path
          d="M27 15L30 18L27 21L24 18L27 15Z"
          fill="#111827"
        />

        <path
          d="M20 15L25 20L20 25L15 20L20 15Z"
          fill="#111827"
        />

        <path
          d="M20 24L24 28L20 32L16 28L20 24Z"
          fill="#111827"
        />
      </svg>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | BTC
  |--------------------------------------------------------------------------
  */

  if (type === "BTC") {
    return (
      <svg {...common}>
        <circle
          cx="20"
          cy="20"
          r="18"
          fill="#F7931A"
        />

        <text
          x="20"
          y="27"
          textAnchor="middle"
          fontSize="22"
          fontWeight="900"
          fill="white"
          fontFamily="Arial"
        >
          ₿
        </text>
      </svg>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | SOL
  |--------------------------------------------------------------------------
  */

  if (type === "SOL") {
    return (
      <svg {...common}>
        <defs>
          <linearGradient
            id="solGradientWallet"
            x1="5"
            y1="5"
            x2="35"
            y2="35"
          >
            <stop
              offset="0%"
              stopColor="#00FFA3"
            />

            <stop
              offset="100%"
              stopColor="#9945FF"
            />
          </linearGradient>
        </defs>

        <circle
          cx="20"
          cy="20"
          r="18"
          fill="#111827"
        />

        <path
          d="M10 13H29L25.5 16.5H10L13.5 13Z"
          fill="url(#solGradientWallet)"
        />

        <path
          d="M11 19H30L26.5 22.5H11L14.5 19Z"
          fill="url(#solGradientWallet)"
        />

        <path
          d="M10 25H29L25.5 28.5H10L13.5 25Z"
          fill="url(#solGradientWallet)"
        />
      </svg>
    );
  }

  return null;
};

/*
|--------------------------------------------------------------------------
| PAGE
|--------------------------------------------------------------------------
*/

export default function CryptoWallet() {
  const navigate = useNavigate();
  const location = useLocation();

  const [language, setLanguage] = useState(
    () => localStorage.getItem("language") || "en"
  );

  useEffect(() => {
    const updateLanguage = () => {
      setLanguage(localStorage.getItem("language") || "en");
    };

    window.addEventListener("languageChanged", updateLanguage);
    window.addEventListener("storage", updateLanguage);

    return () => {
      window.removeEventListener("languageChanged", updateLanguage);
      window.removeEventListener("storage", updateLanguage);
    };
  }, []);

  const tr = (key, vars = {}) => {
    const langPack = WALLET_TRANSLATIONS[language] || {};
    const template =
      langPack[key] ?? WALLET_TRANSLATIONS.en[key] ?? key;

    return String(template).replace(/\{(\w+)\}/g, (_, name) =>
      vars[name] === undefined ? `{${name}}` : String(vars[name])
    );
  };

  /*
  |--------------------------------------------------------------------------
  | METHOD FROM DEPOSIT PAGE
  |--------------------------------------------------------------------------
  */

  const methodFromState =
    location.state?.method;

  const existingFromState =
    location.state?.existingAddress || "";

  /*
  |--------------------------------------------------------------------------
  | SELECTED METHOD
  |--------------------------------------------------------------------------
  */

  const selected = useMemo(() => {
    if (!methodFromState) {
      return null;
    }

    return (
      DEPOSIT_METHODS.find(
        (item) =>
          item.id === methodFromState.id
      ) || methodFromState
    );
  }, [methodFromState]);

  /*
  |--------------------------------------------------------------------------
  | STATES
  |--------------------------------------------------------------------------
  */

  const [username, setUsername] =
    useState("");

  const [userDocId, setUserDocId] =
    useState("");

  const [address, setAddress] =
    useState(existingFromState);

  const [amount, setAmount] =
    useState("");

  const [loading, setLoading] =
    useState(true);

  const [generatingWallet, setGeneratingWallet] =
    useState(false);

  const [depositLoading, setDepositLoading] =
    useState(false);

  const [showDepositDetails, setShowDepositDetails] =
    useState(false);

  const [showAllAmountShortcuts, setShowAllAmountShortcuts] =
    useState(false);

  const [copied, setCopied] =
    useState(false);

  const [error, setError] =
    useState("");

  const [message, setMessage] =
    useState("");

  /*
  |--------------------------------------------------------------------------
  | LOAD USER
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (!selected) {
      navigate(
        "/deposit-crypto",
        {
          replace: true,
        }
      );

      return;
    }

    loadUser();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  /*
  |--------------------------------------------------------------------------
  | LOAD USER FUNCTION
  |--------------------------------------------------------------------------
  */

  const loadUser = async () => {
    try {
      setLoading(true);
      setError("");

      const savedUsername =
        localStorage.getItem(
          "username"
        );

      if (!savedUsername) {
        setError(
          tr("notLoggedIn")
        );

        setLoading(false);
        return;
      }

      setUsername(savedUsername);

      const q = query(
        collection(
          db,
          "users"
        ),
        where(
          "username",
          "==",
          savedUsername
        )
      );

      const snapshot =
        await getDocs(q);

      if (snapshot.empty) {
        setError(
          tr("userNotFound")
        );

        setLoading(false);
        return;
      }

      const userDoc =
        snapshot.docs[0];

      const data =
        userDoc.data();

      setUserDocId(
        userDoc.id
      );

      /*
      |--------------------------------------------------------------------------
      | GET EXISTING WALLET
      |--------------------------------------------------------------------------
      */

      if (selected) {
        const oldAddress =
          data?.[
            selected.field
          ];

        const walletAddress =
          data?.cryptoWallets?.[
            selected.walletKey
          ]?.address;

        const finalAddress =
          oldAddress ||
          walletAddress ||
          "";

        if (
          finalAddress &&
          String(finalAddress).trim()
        ) {
          setAddress(
            String(
              finalAddress
            ).trim()
          );
        }
      }

      setLoading(false);

    } catch (err) {
      console.error(
        "Load wallet user error:",
        err
      );

      setError(
        tr("failedLoad")
      );

      setLoading(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | GENERATE WALLET
  |--------------------------------------------------------------------------
  */

  const generateWallet = async () => {
    if (!selected) {
      return;
    }

    if (generatingWallet) {
      return;
    }

    try {
      setGeneratingWallet(true);
      setError("");
      setMessage("");

      /*
      |--------------------------------------------------------------------------
      | GET LATEST USER
      |--------------------------------------------------------------------------
      */

      const q = query(
        collection(
          db,
          "users"
        ),
        where(
          "username",
          "==",
          username
        )
      );

      const snapshot =
        await getDocs(q);

      if (snapshot.empty) {
        throw new Error(
          tr("walletMissing")
        );
      }

      const userDoc =
        snapshot.docs[0];

      const latestUserData =
        userDoc.data();

      /*
      |--------------------------------------------------------------------------
      | CHECK EXISTING WALLET
      |--------------------------------------------------------------------------
      */

      const existingAddress =
        latestUserData?.[
          selected.field
        ] ||
        latestUserData?.cryptoWallets?.[
          selected.walletKey
        ]?.address ||
        "";

      if (
        existingAddress &&
        String(
          existingAddress
        ).trim()
      ) {
        setAddress(
          String(
            existingAddress
          ).trim()
        );

        setUserDocId(
          userDoc.id
        );

        return;
      }

      /*
      |--------------------------------------------------------------------------
      | WALLET INDEX
      |--------------------------------------------------------------------------
      */

      let walletIndex =
        latestUserData?.walletIndex;

      if (
        walletIndex === undefined ||
        walletIndex === null ||
        walletIndex === ""
      ) {
        walletIndex =
          Math.floor(
            Date.now() % 1000000
          );
      }

      /*
      |--------------------------------------------------------------------------
      | CALL WALLET SERVER
      |--------------------------------------------------------------------------
      */

      const response =
        await fetch(
          `${SERVER_URL}/wallet`,
          {
            method: "POST",

            headers: {
              "Content-Type":
                "application/json",
            },

            body: JSON.stringify({
              index: walletIndex,
            }),
          }
        );

      if (!response.ok) {
        throw new Error(
          `Wallet server returned ${response.status}`
        );
      }

      const data =
        await response.json();

      if (
        !data?.success ||
        !data?.wallets
      ) {
        throw new Error(
          tr("walletFailed")
        );
      }

      const wallets =
        data.wallets;

      /*
      |--------------------------------------------------------------------------
      | SELECT ADDRESS
      |--------------------------------------------------------------------------
      */

      const generatedAddress =
        wallets?.[
          selected.walletKey
        ]?.address || "";

      if (!generatedAddress) {
        throw new Error(
          `No ${selected.coin} address was returned by wallet server.`
        );
      }

      /*
      |--------------------------------------------------------------------------
      | PRESERVE WALLETS
      |--------------------------------------------------------------------------
      */

      const cryptoWallets = {
        ...(
          latestUserData?.cryptoWallets ||
          {}
        ),
        ...wallets,
      };

      /*
      |--------------------------------------------------------------------------
      | FIRESTORE DATA
      |--------------------------------------------------------------------------
      */

      const updateData = {
        walletIndex,
        cryptoWallets,

        trc20Address:
          wallets?.TRC20?.address ||
          latestUserData?.trc20Address ||
          "",

        trxAddress:
          wallets?.TRON_TRX?.address ||
          latestUserData?.trxAddress ||
          "",

        erc20Address:
          wallets?.ERC20?.address ||
          latestUserData?.erc20Address ||
          "",

        bep20Address:
          wallets?.BNB_BEP20?.address ||
          latestUserData?.bep20Address ||
          "",

        bitcoinAddress:
          wallets?.BITCOIN?.address ||
          latestUserData?.bitcoinAddress ||
          "",

        solanaAddress:
          wallets?.SOLANA?.address ||
          latestUserData?.solanaAddress ||
          "",
      };

      /*
      |--------------------------------------------------------------------------
      | SAVE TO FIRESTORE
      |--------------------------------------------------------------------------
      */

      await updateDoc(
        userDoc.ref,
        updateData
      );

      /*
      |--------------------------------------------------------------------------
      | UPDATE STATE
      |--------------------------------------------------------------------------
      */

      setUserDocId(
        userDoc.id
      );

      setAddress(
        generatedAddress
      );

      setMessage(
        tr("walletCreated", { coin: selected.name })
      );

    } catch (err) {
      console.error(
        "Generate wallet error:",
        err
      );

      if (
        err?.message?.includes(
          "Failed to fetch"
        )
      ) {
        setError(
          tr("cannotConnect", { url: SERVER_URL })
        );
      } else {
        setError(
          err?.message ||
          tr("failedCreate")
        );
      }

    } finally {
      setGeneratingWallet(
        false
      );
    }
  };

  /*
  |--------------------------------------------------------------------------
  | CONTINUE
  |--------------------------------------------------------------------------
  */

  const handleContinue = (e) => {
    e.preventDefault();

    setError("");
    setMessage("");

    const numericAmount =
      Number(amount);

    /*
    |--------------------------------------------------------------------------
    | VALIDATE AMOUNT
    |--------------------------------------------------------------------------
    */

    if (
      !numericAmount ||
      numericAmount < selected.min
    ) {
      setError(
        tr("minimumError", { min: selected.min })
      );

      return;
    }

    /*
    |--------------------------------------------------------------------------
    | WALLET MUST EXIST
    |--------------------------------------------------------------------------
    */

    if (!address) {
      setError(
        tr("addressMissing")
      );

      return;
    }

    /*
    |--------------------------------------------------------------------------
    | SHOW QR + ADDRESS
    |--------------------------------------------------------------------------
    */

    setShowDepositDetails(
      true
    );
  };

  /*
  |--------------------------------------------------------------------------
  | COPY ADDRESS
  |--------------------------------------------------------------------------
  */

  const copyAddress = async () => {
    if (!address) {
      return;
    }

    try {
      if (
        navigator.clipboard &&
        window.isSecureContext
      ) {
        await navigator.clipboard.writeText(
          address
        );
      } else {
        const textArea =
          document.createElement(
            "textarea"
          );

        textArea.value =
          address;

        textArea.style.position =
          "fixed";

        textArea.style.left =
          "-9999px";

        textArea.style.top =
          "0";

        textArea.style.opacity =
          "0";

        document.body.appendChild(
          textArea
        );

        textArea.focus();
        textArea.select();

        document.execCommand(
          "copy"
        );

        document.body.removeChild(
          textArea
        );
      }

      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 2200);

    } catch (err) {
      console.error(
        "Copy failed:",
        err
      );

      setError(
        tr("failedCopy")
      );
    }
  };

  /*
  |--------------------------------------------------------------------------
  | QR CODE
  |--------------------------------------------------------------------------
  */

  const qrUrl = address
    ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(
        address
      )}`
    : "";

  /*
  |--------------------------------------------------------------------------
  | DEPOSIT REQUEST
  |--------------------------------------------------------------------------
  */

  const handleDepositRequest =
    async (e) => {
      e.preventDefault();

      if (!selected) {
        setError(
          tr("depositMethodMissing")
        );

        return;
      }

      if (!address) {
        setError(
          tr("addressMissing")
        );

        return;
      }

      const numericAmount =
        Number(amount);

      if (
        !numericAmount ||
        numericAmount < selected.min
      ) {
        setError(
          tr("minimumError", { min: selected.min })
        );

        return;
      }

      try {
        setDepositLoading(
          true
        );

        setError("");
        setMessage("");

        /*
        |--------------------------------------------------------------------------
        | SAVE DEPOSIT
        |--------------------------------------------------------------------------
        */

        await addDoc(
          collection(
            db,
            "usdt_transactions"
          ),
          {
            username,
            userId: userDocId,
        
            method: selected.id,
            coin: selected.coin,
            network: selected.network,
        
            address,
        
            amount: numericAmount,
        
            status: "processing",
        
            createdAt: serverTimestamp(),
          }
        );

        /*
        |--------------------------------------------------------------------------
        | SUCCESS
        |--------------------------------------------------------------------------
        */

        setMessage(
          tr("requestSubmitted")
        );

        setShowDepositDetails(
          true
        );

      } catch (err) {
        console.error(
          "Deposit request error:",
          err
        );

        setError(
          tr("failedSubmit")
        );

      } finally {
        setDepositLoading(
          false
        );
      }
    };

  /*
  |--------------------------------------------------------------------------
  | LOADING
  |--------------------------------------------------------------------------
  */

  if (loading) {
    return (
      <div className="crypto-wallet-page">
        <div className="crypto-wallet-loading">

          <div className="deposit-spinner" />

          <p>
            {tr("loadingWallet")}
          </p>

        </div>
      </div>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | NO METHOD
  |--------------------------------------------------------------------------
  */

  if (!selected) {
    return null;
  }

  /*
  |--------------------------------------------------------------------------
  | RENDER
  |--------------------------------------------------------------------------
  */

  return (
    <div className="crypto-wallet-page">

      <div className="crypto-wallet-container">

        {/* =====================================================
            HEADER
        ===================================================== */}

        <div className="crypto-wallet-header">

          <button
            type="button"
            className="crypto-wallet-back"
            onClick={() =>
              navigate(-1)
            }
          >
            ←
          </button>

          <div>

            <span>
              {tr("cryptoDeposit")}
            </span>

            <h1>
              {selected.name}
            </h1>

            <p>
              {selected.network}
            </p>

          </div>

          <div className="crypto-wallet-icon">

            <CoinLogo
              type={selected.coin}
            />

          </div>

        </div>

        {/* =====================================================
            ERROR
        ===================================================== */}

        {error && (
          <div className="crypto-wallet-alert error">

            <span>
              !
            </span>

            <p>
              {error}
            </p>

            <button
              type="button"
              onClick={() =>
                setError("")
              }
            >
              ×
            </button>

          </div>
        )}

        {/* =====================================================
            SUCCESS
        ===================================================== */}

        {message && (
          <div className="crypto-wallet-alert success">

            <span>
              ✓
            </span>

            <p>
              {message}
            </p>

            <button
              type="button"
              onClick={() =>
                setMessage("")
              }
            >
              ×
            </button>

          </div>
        )}

        {/* =====================================================
            GENERATING WALLET
        ===================================================== */}

        {generatingWallet && (
          <div className="wallet-generating">

            <div className="deposit-spinner" />

            <div>

              <strong>
                {tr("creatingWallet")}
              </strong>

              <span>
                {tr("pleaseWait")}
              </span>

            </div>

          </div>
        )}

        {/* =====================================================
            CREATE WALLET
        ===================================================== */}

        {!generatingWallet &&
          !address && (

          <div className="wallet-create-box">

            <div className="create-wallet-logo">

              <CoinLogo
                type={selected.coin}
              />

            </div>

            <h2>
              {tr("walletNotCreated")}
            </h2>

            <p>
              {tr("walletWillCreate", { coin: selected.coin })}
            </p>

            <button
              type="button"
              onClick={
                generateWallet
              }
              disabled={
                generatingWallet
              }
            >
              {tr("createWallet", { coin: selected.coin })}
            </button>

          </div>
        )}

        {/* =====================================================
            WALLET CONTENT
        ===================================================== */}

        {!generatingWallet &&
          address && (

          <div className="wallet-content">

            {/* =================================================
                WALLET STATUS
            ================================================= */}

            <div className="wallet-status">

              <span>
                ✓
              </span>

              {tr("walletReady")}

            </div>

            {/* =================================================
                STEP 1
                AMOUNT
            ================================================= */}

            {!showDepositDetails && (

              <form
                className="deposit-amount-form"
                onSubmit={
                  handleContinue
                }
              >

                <label>
                  {tr("depositAmount")}
                </label>

                <div className="amount-input">

                  <span>
                    $
                  </span>

                  <input
                    type="number"
                    min={selected.min}
                    step="any"
                    value={amount}
                    onChange={(e) => {

                      setAmount(
                        e.target.value
                      );

                      setError("");

                    }}
                    placeholder={tr("enterAmount", { min: selected.min })}
                  />

                  <span>
                    USD
                  </span>

                </div>

                {/* =================================================
                    AMOUNT SHORTCUTS
                ================================================= */}

                
<div className="amount-shortcuts-wrapper">

<div
className={`amount-shortcuts-scroll ${
  showAllAmountShortcuts ? "show-all" : ""
}`}
>
{AMOUNT_SHORTCUTS
  .filter(
    (value) => value >= selected.min
  )
  .map((value) => (
    <button
      key={value}
      type="button"
      className={`amount-shortcut ${
        Number(amount) === value
          ? "active"
          : ""
      }`}
      onClick={() => {
        setAmount(String(value));
        setError("");
        setMessage("");
      }}
    >
      ${value.toLocaleString()}
    </button>
  ))}
</div>

<button
type="button"
className="amount-shortcuts-more"
onClick={() =>
  setShowAllAmountShortcuts(
    (prev) => !prev
  )
}
aria-label={
  showAllAmountShortcuts
    ? tr("hideShortcuts")
    : tr("showShortcuts")
}
>
{showAllAmountShortcuts ? "‹" : "›"}
</button>

</div>

                <button
                  type="submit"
                  className="deposit-submit"
                >
                  {tr("continue")}
                </button>

              </form>
            )}

            {/* =================================================
                STEP 2
                QR + ADDRESS
            ================================================= */}

            {showDepositDetails && (

              <div className="deposit-details">

                {/* QR */}

                <div className="wallet-qr-wrapper">

                  <div className="wallet-qr">

                    <img
                      src={qrUrl}
                      alt={`${selected.coin} wallet QR`}
                    />

                  </div>

                  <span>
                    {tr("scanDeposit")}
                  </span>

                </div>

                {/* ADDRESS TITLE */}

                <div className="wallet-address-title">

                  {tr("depositAddress", { coin: selected.coin })}

                </div>

                {/* ADDRESS */}

                <div className="wallet-address-box">

                  <span>
                    {address}
                  </span>

                  <button
                    type="button"
                    onClick={
                      copyAddress
                    }
                  >
                    {copied
                      ? tr("copied")
                      : tr("copy")}
                  </button>

                </div>

                {/* WARNING */}

                <div className="wallet-warning">

                  <span>
                    ⚠️
                  </span>

                  <p>

                    {tr("sendOnly", { coin: selected.coin, network: selected.network })}

                  </p>

                </div>

                {/* AMOUNT SUMMARY */}

                <div className="deposit-summary">

                  <span>
                    {tr("depositAmount")}
                  </span>

                  <strong>

                    $
                    {Number(
                      amount
                    ).toFixed(2)}

                    {" "}USD

                  </strong>

                </div>

                {/* CHANGE AMOUNT */}

                <button
                  type="button"
                  className="deposit-change-amount"
                  onClick={() => {

                    setShowDepositDetails(
                      false
                    );

                    setError("");
                    setMessage("");

                  }}
                >
                  {tr("changeAmount")}
                </button>

                {/* DEPOSIT BUTTON */}

                <button
                  type="button"
                  className="deposit-submit"
                  disabled={
                    depositLoading
                  }
                  onClick={
                    handleDepositRequest
                  }
                >

                  {depositLoading
                    ? tr("submitting")
                    : tr("madeDeposit")}

                </button>

              </div>
            )}

          </div>
        )}

        {/* =====================================================
            PROMOTION / BONUS AREA
        ===================================================== */}

        <div className="crypto-wallet-page-promo">

          {/* =================================================
              MAIN PROMOTION
          ================================================= */}

          <div className="crypto-wallet-promo-card crypto-wallet-promo-main">

            <div className="crypto-wallet-promo-content">

              <span className="crypto-wallet-promo-label">
                {tr("specialOffer")}
              </span>

              <h3>
                {tr("depositBonus")}
              </h3>

              <p>
                {tr("qualifyingDeposit")}
              </p>

              <button
                type="button"
                onClick={() =>
                  navigate("/offers")
                }
              >
                {tr("viewOffers")}
              </button>

            </div>

            <div className="crypto-wallet-promo-icon">
              🎁
            </div>

          </div>

          {/* =================================================
              SMALL PROMOTIONS
          ================================================= */}

          <div className="crypto-wallet-promo-grid">

            {/* FAST DEPOSITS */}

            <div className="crypto-wallet-promo-small">

              <div className="crypto-wallet-promo-small-icon">
                ⚡
              </div>

              <div>

                <strong>
                  {tr("fastDeposits")}
                </strong>

                <span>
                  {tr("secureCrypto")}
                </span>

              </div>

            </div>

            {/* SECURE WALLET */}

            <div className="crypto-wallet-promo-small">

              <div className="crypto-wallet-promo-small-icon">
                🔒
              </div>

              <div>

                <strong>
                  {tr("secureWallet")}
                </strong>

                <span>
                  {tr("fundsProtected")}
                </span>

              </div>

            </div>

          </div>

          {/* =================================================
              BONUS OFFERS
          ================================================= */}

          <div className="crypto-wallet-bonus-section">

            {/* =================================================
                BONUS TITLE
            ================================================= */}

            <div className="crypto-wallet-bonus-header">

              <span>
                {tr("bonusProgram")}
              </span>

              <h2>
                {tr("earnMore")}
              </h2>

              <p>
                {tr("chooseBonus")}
              </p>

            </div>

            {/* =================================================
                {tr("firstDeposit")}
            ================================================= */}

            <div className="crypto-wallet-bonus-card crypto-wallet-bonus-first">

              <div className="crypto-wallet-bonus-icon">
                🎁
              </div>

              <div className="crypto-wallet-bonus-content">

                <span className="crypto-wallet-bonus-tag">
                  {tr("firstDeposit")}
                </span>

                <h3>
                  {tr("firstBonus")}
                </h3>

                <p>

                  {tr("getBonus")}

                  <strong>
                    {" "}10% - 98%
                  </strong>

                  {" "}{tr("firstQualifying")}

                </p>

                <div className="crypto-wallet-bonus-rate">
                  10% - 98%
                </div>

              </div>

            </div>

            {/* =================================================
                {tr("secondDeposit")}
            ================================================= */}

            <div className="crypto-wallet-bonus-card crypto-wallet-bonus-second">

              <div className="crypto-wallet-bonus-icon">
                💰
              </div>

              <div className="crypto-wallet-bonus-content">

                <span className="crypto-wallet-bonus-tag">
                  {tr("secondDeposit")}
                </span>

                <h3>
                  {tr("secondBonus")}
                </h3>

                <p>

                  {tr("secondQualifying")}

                  <strong>
                    {" "}8% - 87%
                  </strong>

                  {" "}{tr("bonus")}

                </p>

                <div className="crypto-wallet-bonus-rate">
                  8% - 87%
                </div>

              </div>

            </div>

            {/* =================================================
                WEEKLY WEDNESDAY BONUS
            ================================================= */}

            <div className="crypto-wallet-bonus-card crypto-wallet-bonus-wednesday">

              <div className="crypto-wallet-bonus-icon">
                📅
              </div>

              <div className="crypto-wallet-bonus-content">

                <span className="crypto-wallet-bonus-tag">
                  {tr("everyWednesday")}
                </span>

                <h3>
                  {tr("weeklyBonus")}
                </h3>

                <p>

                  {tr("wednesdayDeposit")}

                  <strong>
                    {" "}5% - 56%
                  </strong>.

                </p>


                

                <div className="crypto-wallet-bonus-rate">
                  5% - 56%
                </div>

              </div>

            </div>

          </div>


          

          <button
type="button"
className="deposit-submit"
onClick={handleContinue}
disabled={!address || !amount}
>
{tr("continue")}
</button>




        </div>

      </div>

    </div>
  );
}
